import React from 'react';
import mapboxgl from 'mapbox-gl';
import { TrailPlanner } from '../../../../../utils/trail_planner/trail_planner';
import { putAtMapIntoBackground, bringAtMapIntoForeground } from '../../../../../utils/mapbox/overlays/at_map';
import { addDistanceMarkers } from '../../../../../utils/mapbox/overlays/distance_markers';
import { mountReactComponent, getPopupDOMContent, unmountReactComponent } from '../../../../../utils/mapbox/event_handling/popup_helpers';
import SearchPointPopup from '../../../../../components/MapCreator/SearchPointPopup';

import { updateLayerProperty, removeLayer } from '../../../../../utils/mapbox/layers/layer_helpers';
import {
  addTrailPlannerOverlay,
  removeTrailPlannerMap,
  routeNodesId,
  routeNodesDragId,
  newRouteNodeId
} from '../../../../../utils/mapbox/overlays/trail_planner_overlay';
import { setWindowExitGuard } from '../../../../../utils/window_helpers';
import { atMapsToGeojson } from '../../../../../utils/mapbox/sources/at_maps';

const TrailPlannerMixin = {
  trailPlannerMessagingSubscriptions() {
    if (this.listeners == null) this.listeners = [];
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.undo', this.undoClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.redo', this.redoClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.out_and_back', this.outAndBackClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.return', this.returnClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.reverse', this.reverseClick));
    this.listeners.push(this.props.messagingChannel.subscribe('map.click', this.mapClick));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.start', this.mapShiftDragStart));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.end', this.mapShiftDragEnd));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.move', this.mapShiftDragMove));
    this.listeners.push(this.props.messagingChannel.subscribe('map.ctrl_key', this.mapCtrlKey));
    this.listeners.push(this.props.messagingChannel.subscribe('map.del_key', this.mapDelKey));
    this.listeners.push(this.props.messagingChannel.subscribe('route.edit.changeColor', this.handleRouteColorChange));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.enable_trail_planner', this.enablePlanning));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.disable_trail_planner', this.disablePlanning));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.show_popup_at_lat_lng', this.showPopupAtLatLng));
  },
  showPopupAtLatLng(data) {
    const [lat, lng, placeName, zoom = 15] = data;
    const partialPlaceName = placeName.split(',')[0];
    const isStartingPoint = !this.state.trailPlanner.routeStart;
    const popupOptions = { closeButton: false, closeOnClick: false, offset: 15 };

    // different zoom behavior based on whether or not route is starting
    if (isStartingPoint) {
      this.setMapCenterAndZoom(lat, lng, zoom);
    } else {
      const bounds = this.mbMap.getBounds();
      this.mbMap.fitBounds(bounds.extend([lng, lat]), { padding: 200 });
    }

    const markerDot = document.createElement('div');
    markerDot.classList.add(isStartingPoint ? 'mapboxgl-green-dot-marker' : 'mapboxgl-gray-dot-marker');

    // create new marker
    const searchPointMarker = new mapboxgl.Marker({
      draggable: true,
      element: markerDot
    });

    const removeMarkerAndUnmountPopup = (marker, domContent) => {
      unmountReactComponent(domContent);
      marker.remove();
    };

    const addPointAndRemoveMarkerAndPopup = (coordinates, marker, domContent) => {
      const [longitude, latitude] = coordinates;
      this.state.trailPlanner.routeToPoint([longitude, latitude]);

      removeMarkerAndUnmountPopup(marker, domContent);
    };

    const DOMContent = mountReactComponent(
      <SearchPointPopup
        id="search-popup"
        onClose={() => removeMarkerAndUnmountPopup(searchPointMarker, DOMContent)}
        isStartingPoint={isStartingPoint}
        locationName={partialPlaceName}
        addToRoute={() => addPointAndRemoveMarkerAndPopup([lng, lat], searchPointMarker, DOMContent)}
      />
    );

    // create popup
    const initialPopup = new mapboxgl.Popup(popupOptions).setDOMContent(DOMContent);
    // add popup to marker
    searchPointMarker
      .setLngLat([lng, lat])
      .setPopup(initialPopup)
      .addTo(this.mbMap)
      .togglePopup();

    searchPointMarker.on('dragend', () => {
      // unmount current popup component
      const currentPopupContent = getPopupDOMContent(searchPointMarker.getPopup());
      unmountReactComponent(currentPopupContent);

      const coordinates = searchPointMarker.getLngLat();

      // update the domContent
      const updatedDOMContent = mountReactComponent(
        <SearchPointPopup
          onClose={() => removeMarkerAndUnmountPopup(searchPointMarker, updatedDOMContent)}
          isStartingPoint={isStartingPoint}
          addToRoute={() => addPointAndRemoveMarkerAndPopup([coordinates.lng, coordinates.lat], searchPointMarker, updatedDOMContent)}
        />
      );
      // update popup with new domContent
      initialPopup.setLngLat([coordinates.lng, coordinates.lat]).setDOMContent(updatedDOMContent);
    });
  },
  enablePlanning(route) {
    if (this.state.trailPlanner) {
      return;
    }

    setWindowExitGuard(true);

    // Update map's default cursor to 'crosshair'
    this.mbMap.customStyle.cursor = 'crosshair';
    this.mbMap.getCanvas().style.cursor = this.mbMap.customStyle.cursor;

    putAtMapIntoBackground(this.mbMap);

    const { updateProgressDialog } = this.props;
    const { routingActive, routingMode } = this.state;
    const onRouteInfoChange = this.onRouteInfoChange;
    // Attach to state immediately to prevent any race condition issues
    this.state.trailPlanner = new TrailPlanner({
      onRouteInfoChange,
      route,
      routingActive,
      routingMode,
      updateProgressDialog
    });
    this.state.trailPlanner.initRoutePolylines();
    this.setState({
      trailPlanner: this.state.trailPlanner,
      currentlyEditingRoute: route,
      selectedRouteNode: null
    });
  },
  disablePlanning() {
    if (!this.state.trailPlanner) {
      return;
    }

    // Restore map's default cursor to ''
    this.mbMap.customStyle.cursor = '';
    this.mbMap.getCanvas().style.cursor = this.mbMap.customStyle.cursor;

    removeTrailPlannerMap(this.mbMap);
    bringAtMapIntoForeground(this.mbMap);
    this.updateDistanceMarkers(this.props.exploreMap);

    setWindowExitGuard(false);
    $('#flash-msg').slideUp();

    this.props.handleRouteInfoChanged(null);
    this.setState({
      trailPlanner: null,
      currentlyEditingRoute: null,
      selectedRouteNode: null
    });
  },
  handleRouteColorChange(newColor) {
    this.state.trailPlanner.color = newColor;
    // Update drawn route
    this.drawTrailPlannerRoute();
    // Update plannerMap at parent level
    const plannerMap = this.state.trailPlanner.toAtMap();
    this.props.handleRouteInfoChanged({ plannerMap });
  },
  onRouteInfoChange(routeInfo) {
    this.drawTrailPlannerRoute();
    this.updateDistanceMarkers(routeInfo.plannerMap);
    if (routeInfo.fullChange) {
      this.props.handleRouteInfoChanged(routeInfo);
      this.setState({
        plannerMap: routeInfo.plannerMap,
        selectedRouteNode: null
      });
    }
  },
  drawTrailPlannerRoute() {
    addTrailPlannerOverlay(this.mbMap, this.state.trailPlanner, this.state.trailPlannerColor);
  },
  updateDistanceMarkers(atMap) {
    if (this.state.enabledOverlays.includes('distanceMarkers')) {
      addDistanceMarkers(this.mbMap, atMapsToGeojson([atMap]));
    }
  },
  routeNodeClick(feature) {
    const routeNode = feature.properties;
    removeLayer(this.mbMap, `${routeNodesDragId}-labels`);
    if (feature.id === newRouteNodeId) {
      // Split route at new route node
      this.state.trailPlanner.splitRouteAtNewNode({ ...routeNode, lngLat: feature.geometry.coordinates });
    } else {
      // Toggle selection of route node
      this.toggleSelectRouteNode(routeNode);
    }
  },
  mapClick(e) {
    if (this.state.trailPlanner) {
      if (!this.mbMap.shiftKeyDown) {
        // Handle clicking on specific trail planner layers (like a routeNode)
        const features = this.mbMap.queryRenderedFeatures(e.point);
        const feature = features[0];
        if (feature && [routeNodesId, routeNodesDragId].includes(feature.layer.id)) {
          this.routeNodeClick(feature);
          return;
        }
      }

      // Drawing functionality
      this.state.trailPlanner.routeToPoint(e.lngLat.toArray(), true);
    }
  },
  mapShiftDragStart(e) {
    if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.startDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapShiftDragMove(e) {
    if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.updateDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapShiftDragEnd(e) {
    if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.endDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapCtrlKey(pressed) {
    if (pressed) {
      this.toggleRouting();
    }
  },
  mapDelKey(pressed) {
    if (this.state.trailPlanner && this.state.selectedRouteNode && pressed) {
      this.state.trailPlanner.routeNodeRemoved(this.state.selectedRouteNode);
      this.toggleSelectRouteNode(this.state.selectedRouteNode);
    }
  },
  toggleSelectRouteNode(routeNode) {
    if (this.state.selectedRouteNode && this.state.selectedRouteNode.id === routeNode.id) {
      // Deselect
      updateLayerProperty(this.mbMap, routeNodesId, 'layout', 'icon-size', 1);
      this.setState({ selectedRouteNode: null });
    } else {
      // Select
      const iconSize = ['match', ['get', 'id'], routeNode.id, 1.25, 1];
      updateLayerProperty(this.mbMap, routeNodesId, 'layout', 'icon-size', iconSize);
      this.setState({ selectedRouteNode: routeNode });
    }
  },
  toggleRouting() {
    const routingActive = !this.state.routingActive;
    this.setState({ routingActive });
    if (this.state.trailPlanner) {
      this.state.trailPlanner.routingActive = routingActive;
    }
  },
  toggleRoutingTip(tipType, e) {
    e.stopPropagation();
    const toolTipState = { ...this.state.showRoutingTips };
    toolTipState[tipType] = false;
    this.setState({ showRoutingTips: toolTipState });
  },
  setRoutingMode(e) {
    const routingMode = e.target.value;
    this.setState({ routingMode });
    if (this.state.trailPlanner) {
      this.state.trailPlanner.routingMode = routingMode;
    }
  },
  outAndBackClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.outAndBack();
    }
  },
  returnClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.returnToStart();
    }
  },
  reverseClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.reverseRoute();
    }
  },
  undoClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.undo();
    }
  },
  redoClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.redo();
    }
  }
};

export { TrailPlannerMixin };
